import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { IndianRupee, Percent } from 'lucide-react';
import {
  FeeAdjustmentGroupRow,
  FeeAdjustmentInstallment,
  FeeDemandRow,
  RecordFeePaymentPayload,
  ApplyFeeDiscountPayload,
} from '@/services/fees.api';
import { Badge } from '@/components/ui/Badge';
import { Input, Select, Textarea } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { UploadApi } from '@/services/school.api';
import { fmtMoney, fmtDate } from './feeFormat';

/** What Payment/Discount modals act on — always ONE installment (a real fee_demands.id), never a whole group. */
export interface InstallmentTarget {
  id: string;
  studentName: string;
  feeStructureName: string;
  installmentName: string | null;
  actualAmount: number;
  balance: number;
}

/**
 * Shared by Fees Adjustment and Noorani Fees — both act on individual
 * fee_demands rows via the exact same payment/discount logic (a demand's
 * handling never depended on which fee it's for), so the modals take the
 * mutation function as a prop rather than being duplicated per screen.
 */

/**
 * Matches the old system's fee-adjustments payment modal exactly: one
 * modal per (student, fee, year) group showing every installment's own
 * Name/Date/Actual/Paid/Balance/Discount, with Pay/Discount actions per
 * row — never a single flat total for the group.
 */
export function InstallmentsModal({
  group,
  onClose,
  onPay,
  onDiscount,
}: {
  group: FeeAdjustmentGroupRow;
  onClose: () => void;
  onPay: (inst: FeeAdjustmentInstallment) => void;
  onDiscount: (inst: FeeAdjustmentInstallment) => void;
}) {
  return (
    <Modal
      open
      onClose={onClose}
      title="Installment Breakdown"
      description={`${group.studentName} — ${group.feeStructureName} · ${group.academicYearName}`}
      size="xl"
      footer={
        <button className="btn-secondary" onClick={onClose}>
          Close
        </button>
      }
    >
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
              <th className="py-2 pr-3">Installment Name</th>
              <th className="py-2 pr-3">Date</th>
              <th className="py-2 pr-3">Actual Amount</th>
              <th className="py-2 pr-3">Paid Amount</th>
              <th className="py-2 pr-3">Balance</th>
              <th className="py-2 pr-3">Discount</th>
              <th className="py-2 pr-3" />
            </tr>
          </thead>
          <tbody>
            {group.installments.map((inst) => (
              <tr key={inst.id} className="border-b border-slate-100 last:border-0">
                <td className="py-2 pr-3">{inst.installmentName ?? '—'}</td>
                <td className="py-2 pr-3">{fmtDate(inst.dueDate)}</td>
                <td className="py-2 pr-3">{fmtMoney(inst.actualAmount)}</td>
                <td className="py-2 pr-3">{fmtMoney(inst.paidAmount)}</td>
                <td className="py-2 pr-3">
                  <Badge tone={inst.balance <= 0 ? 'green' : 'amber'}>{fmtMoney(inst.balance)}</Badge>
                </td>
                <td className="py-2 pr-3">
                  {inst.discountAmount ? (
                    <span title={inst.discountReason ?? undefined}>{fmtMoney(Math.abs(inst.discountAmount))}</span>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="py-2 pr-3">
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-green-700"
                      title="Record Payment"
                      onClick={() => onPay(inst)}
                    >
                      <IndianRupee className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-blue-700"
                      title="Apply Discount"
                      onClick={() => onDiscount(inst)}
                    >
                      <Percent className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}

export function PaymentModal({
  target,
  onClose,
  onSaved,
  recordPayment,
}: {
  target: InstallmentTarget;
  onClose: () => void;
  onSaved: () => void;
  recordPayment: (demandId: string, payload: RecordFeePaymentPayload) => Promise<FeeDemandRow>;
}) {
  const [amount, setAmount] = useState(String(target.balance > 0 ? target.balance : ''));
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [mode, setMode] = useState('Cash');
  const [reference, setReference] = useState('');
  const [image, setImage] = useState<string | undefined>(undefined);
  const [uploading, setUploading] = useState(false);

  const save = useMutation({
    mutationFn: (payload: RecordFeePaymentPayload) => recordPayment(target.id, payload),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Record Payment"
      description={`${target.studentName} — ${target.feeStructureName}${target.installmentName ? ` · ${target.installmentName}` : ''}`}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={save.isPending || !amount || !date || !mode}
            onClick={() => save.mutate({ amount: Number(amount), date, mode, reference: reference || undefined, image })}
          >
            {save.isPending ? 'Saving…' : 'Record Payment'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Amount</label>
          <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={`Balance: ${target.balance}`} />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Date</label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Mode</label>
          <Select value={mode} onChange={(e) => setMode(e.target.value)}>
            <option value="Cash">Cash</option>
            <option value="Online">Online</option>
            <option value="Cheque">Cheque</option>
            <option value="Card">Card</option>
          </Select>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Reference</label>
          <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Optional" />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Image</label>
          <input
            type="file"
            accept="image/*"
            disabled={uploading}
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              setUploading(true);
              try {
                const { url } = await UploadApi.upload(f);
                setImage(url);
              } finally {
                setUploading(false);
              }
            }}
          />
          {uploading && <span className="text-xs text-slate-400">Uploading…</span>}
          {image && !uploading && <span className="text-xs text-green-600">Uploaded</span>}
        </div>
        {save.error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">Could not save payment.</div>}
      </div>
    </Modal>
  );
}

export function DiscountModal({
  target,
  onClose,
  onSaved,
  applyDiscount,
}: {
  target: InstallmentTarget;
  onClose: () => void;
  onSaved: () => void;
  applyDiscount: (demandId: string, payload: ApplyFeeDiscountPayload) => Promise<FeeDemandRow>;
}) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');

  const save = useMutation({
    mutationFn: (payload: ApplyFeeDiscountPayload) => applyDiscount(target.id, payload),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Apply Discount"
      description={`${target.studentName} — ${target.feeStructureName}${target.installmentName ? ` · ${target.installmentName}` : ''}`}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={save.isPending || !amount || !reason}
            onClick={() => save.mutate({ amount: Number(amount), reason })}
          >
            {save.isPending ? 'Saving…' : 'Apply Discount'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Discount amount</label>
          <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={`Actual: ${target.actualAmount}`} />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Reason</label>
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>
        {save.error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">Could not save discount.</div>}
      </div>
    </Modal>
  );
}
